import { Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

interface SquareCustomerAutosyncSettingProps {
  enabled: boolean;
  isLoading: boolean;
  isError: boolean;
  isSaving: boolean;
  onChange: (next: boolean) => void;
}

/**
 * On/off switch for the Square customer auto-sync sweep (#827), shown in
 * Settings > Square Integration. The sweep itself runs server-side every few
 * minutes (square-customers-autosync); this only flips the app setting.
 */
export function SquareCustomerAutosyncSetting({
  enabled,
  isLoading,
  isError,
  isSaving,
  onChange,
}: SquareCustomerAutosyncSettingProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <Label htmlFor="square-customer-autosync" className="text-base font-medium">
          Auto-sync new contacts
        </Label>
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Switch
            id="square-customer-autosync"
            aria-label="Auto-sync new contacts to Square"
            checked={enabled}
            disabled={isError || isSaving}
            onCheckedChange={onChange}
          />
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        When on, contacts added from now on (website requests, online orders, GHL leads and manual adds) are added to
        Square within a few minutes. If Square already has a customer with the same email, the contact is linked to it.
        Contacts without an email are skipped; use Push to Square on the contact. Only runs in Production mode.
      </p>
      {isError && (
        <p className="text-sm text-muted-foreground">Couldn't load this setting. Reload the page to try again.</p>
      )}
    </div>
  );
}
